
import { supabase } from "@/integrations/supabase/client";
import { v4 as uuidv4 } from "uuid";
import { QuestionType } from "../quiz/types";

/**
 * Adds a multiple-select demo question to the workplace safety training quiz
 */
export async function insertDemoMultiSelectQuestion() {
  try {
    // Find the workplace safety quiz
    const { data: quizData, error: quizError } = await supabase
      .from('resource_files')
      .select('quiz_id, resource_id')
      .ilike('title', '%workplace safety%')
      .eq('file_type', 'quiz')
      .limit(1)
      .maybeSingle();
    
    if (quizError) throw quizError;
    
    if (!quizData?.quiz_id) {
      console.error('No workplace safety quiz found');
      return;
    }
    
    console.log('Found workplace safety quiz:', quizData);
    
    // Check if a multiple-select question already exists
    const { data: existingQuestion, error: checkError } = await supabase
      .from('quiz_questions')
      .select('id')
      .eq('quiz_id', quizData.quiz_id)
      .eq('type', 'multiple_select')
      .limit(1)
      .maybeSingle();
    
    if (checkError) throw checkError;
    
    if (existingQuestion?.id) {
      console.log('Multiple-select question already exists for this quiz');
      return;
    }
    
    // Get the highest order index
    const { data: orderData, error: orderError } = await supabase
      .from('quiz_questions')
      .select('order_index')
      .eq('quiz_id', quizData.quiz_id)
      .order('order_index', { ascending: false })
      .limit(1)
      .maybeSingle();
    
    if (orderError) throw orderError;
    
    const newOrderIndex = (orderData?.order_index || 0) + 1;
    
    // Add the multiple-select demo question
    const demoQuestion: {
      id: string;
      quiz_id: string;
      question: string;
      type: QuestionType;
      correct_answer: string;
      options: string[];
      order_index: number;
    } = {
      id: uuidv4(),
      quiz_id: quizData.quiz_id,
      question: "Which of the following are required safety equipment when working in a construction area? (Select all that apply)",
      type: 'multiple_select' as QuestionType,
      // Store correct answers as a JSON string array
      correct_answer: JSON.stringify(["Hard hat", "Safety goggles", "Steel-toed boots"]),
      options: ["Hard hat", "Safety goggles", "Regular sneakers", "Steel-toed boots", "Headphones"],
      order_index: newOrderIndex
    };
    
    const { data, error } = await supabase
      .from('quiz_questions')
      .insert(demoQuestion)
      .select();
    
    if (error) throw error;
    
    console.log('Successfully added multiple-select demo question:', data);
    return data;
    
  } catch (error) {
    console.error('Error adding multiple-select demo question:', error);
    throw error;
  }
}

/**
 * Adds a demo drag and drop game for categorizing safety equipment
 */
export async function insertDemoDragDropGame(resourceId: string) {
  try {
    // Check if a drag and drop game already exists for this resource
    const { data: existingGame, error: checkError } = await supabase
      .from('resource_files')
      .select('id')
      .eq('resource_id', resourceId)
      .eq('file_type', 'drag_drop')
      .limit(1)
      .maybeSingle();
    
    if (checkError) throw checkError;
    
    if (existingGame?.id) {
      console.log('Drag and drop game already exists for this resource');
      return;
    }
    
    // Get the highest order index
    const { data: orderData, error: orderError } = await supabase
      .from('resource_files')
      .select('order_index')
      .eq('resource_id', resourceId)
      .order('order_index', { ascending: false })
      .limit(1)
      .maybeSingle();
    
    if (orderError) throw orderError;
    
    const newOrderIndex = (orderData?.order_index || 0) + 1;
    
    // Create the drag and drop game resource file
    const { data: fileData, error: fileError } = await supabase
      .from('resource_files')
      .insert({
        resource_id: resourceId,
        title: 'Safety Equipment Categories',
        description: 'Drag each item to its correct category',
        file_type: 'drag_drop',
        order_index: newOrderIndex
      })
      .select()
      .single();
    
    if (fileError) throw fileError;
    
    // Create the game record
    const { data: gameData, error: gameError } = await supabase
      .from('drag_drop_games')
      .insert({
        file_id: fileData.id,
        title: 'Safety Equipment Categories',
        description: 'Drag each item to its correct category',
        passing_score: 70
      })
      .select()
      .single();
    
    if (gameError) throw gameError;
    
    // Create categories
    const categories = [
      { name: 'Head Protection', game_id: gameData.id },
      { name: 'Eye Protection', game_id: gameData.id },
      { name: 'Foot Protection', game_id: gameData.id },
      { name: 'Not Required', game_id: gameData.id }
    ];
    
    const { error: categoryError } = await supabase
      .from('drag_drop_categories')
      .insert(categories);
    
    if (categoryError) throw categoryError;
    
    // Create options
    const options = [
      { text: 'Hard hat', correct_category: 'Head Protection', game_id: gameData.id },
      { text: 'Bandana', correct_category: 'Not Required', game_id: gameData.id },
      { text: 'Safety goggles', correct_category: 'Eye Protection', game_id: gameData.id },
      { text: 'Reading glasses', correct_category: 'Not Required', game_id: gameData.id },
      { text: 'Steel-toed boots', correct_category: 'Foot Protection', game_id: gameData.id },
      { text: 'Sneakers', correct_category: 'Not Required', game_id: gameData.id },
      { text: 'Face shield', correct_category: 'Eye Protection', game_id: gameData.id },
      { text: 'Helmet', correct_category: 'Head Protection', game_id: gameData.id }
    ];
    
    const { error: optionsError } = await supabase
      .from('drag_drop_options')
      .insert(options);
    
    if (optionsError) throw optionsError;
    
    console.log('Successfully added demo drag and drop game');
    return gameData;
    
  } catch (error) {
    console.error('Error adding demo drag and drop game:', error);
    throw error;
  }
}

/**
 * Adds a demo linking game for safety procedures
 */
export async function insertDemoLinkingGame(resourceId: string) {
  try {
    // Check if a linking game already exists for this resource
    const { data: existingGame, error: checkError } = await supabase
      .from('resource_files')
      .select('id')
      .eq('resource_id', resourceId)
      .eq('file_type', 'linking')
      .limit(1)
      .maybeSingle();
    
    if (checkError) throw checkError;
    
    if (existingGame?.id) {
      console.log('Linking game already exists for this resource');
      return;
    }
    
    // Get the highest order index
    const { data: orderData, error: orderError } = await supabase
      .from('resource_files')
      .select('order_index')
      .eq('resource_id', resourceId)
      .order('order_index', { ascending: false })
      .limit(1)
      .maybeSingle();
    
    if (orderError) throw orderError;
    
    const newOrderIndex = (orderData?.order_index || 0) + 1;
    
    // Create the linking game resource file
    const { data: fileData, error: fileError } = await supabase
      .from('resource_files')
      .insert({
        resource_id: resourceId,
        title: 'Match Safety Situations with Appropriate Actions',
        description: 'Connect each situation with the appropriate safety response',
        file_type: 'linking',
        order_index: newOrderIndex
      })
      .select()
      .single();
    
    if (fileError) throw fileError;
    
    // Create the game record
    const { data: gameData, error: gameError } = await supabase
      .from('linking_games')
      .insert({
        file_id: fileData.id,
        title: 'Match Safety Situations with Appropriate Actions',
        description: 'Connect each situation with the appropriate safety response',
        passing_score: 70
      })
      .select()
      .single();
    
    if (gameError) throw gameError;
    
    // Create items
    const items = [
      { left_text: 'Chemical spill', right_text: 'Use spill kit and report immediately', game_id: gameData.id },
      { left_text: 'Fire alarm sounds', right_text: 'Evacuate using nearest exit', game_id: gameData.id },
      { left_text: 'Machinery malfunction', right_text: 'Shut down and tag as out of order', game_id: gameData.id },
      { left_text: 'Trip hazard spotted', right_text: 'Secure area and place warning signs', game_id: gameData.id },
      { left_text: 'Injured coworker', right_text: 'Provide first aid and call for help', game_id: gameData.id }
    ];
    
    const { error: itemsError } = await supabase
      .from('linking_items')
      .insert(items);
    
    if (itemsError) throw itemsError;
    
    console.log('Successfully added demo linking game');
    return gameData;
    
  } catch (error) {
    console.error('Error adding demo linking game:', error);
    throw error;
  }
}

/**
 * Adds demo free response questions
 */
export async function insertDemoFreeResponseQuestions(resourceId: string) {
  try {
    // Check if free response questions already exist for this resource
    const { data: existingQuestions, error: checkError } = await supabase
      .from('resource_files')
      .select('id')
      .eq('resource_id', resourceId)
      .eq('file_type', 'free_response')
      .limit(1)
      .maybeSingle();
    
    if (checkError) throw checkError;
    
    if (existingQuestions?.id) {
      console.log('Free response questions already exist for this resource');
      return;
    }
    
    // Get the highest order index
    const { data: orderData, error: orderError } = await supabase
      .from('resource_files')
      .select('order_index')
      .eq('resource_id', resourceId)
      .order('order_index', { ascending: false })
      .limit(1)
      .maybeSingle();
    
    if (orderError) throw orderError;
    
    const newOrderIndex = (orderData?.order_index || 0) + 1;
    
    // Create the free response resource file
    const { data: fileData, error: fileError } = await supabase
      .from('resource_files')
      .insert({
        resource_id: resourceId,
        title: 'Safety Reflection Questions',
        description: 'Please answer the following questions in your own words',
        file_type: 'free_response',
        order_index: newOrderIndex
      })
      .select()
      .single();
    
    if (fileError) throw fileError;
    
    // Create questions
    const questions = [
      { 
        file_id: fileData.id, 
        question: 'What are three safety hazards you might encounter in your specific work area, and how would you address them?', 
        order_index: 0 
      },
      { 
        file_id: fileData.id, 
        question: 'Describe a situation where you had to make a decision about workplace safety. What factors did you consider?', 
        order_index: 1 
      },
      { 
        file_id: fileData.id, 
        question: 'How would you respond if you noticed a coworker not following proper safety protocols?', 
        order_index: 2 
      }
    ];
    
    const { error: questionsError } = await supabase
      .from('free_response_questions')
      .insert(questions);
    
    if (questionsError) throw questionsError;
    
    console.log('Successfully added demo free response questions');
    return fileData;
    
  } catch (error) {
    console.error('Error adding demo free response questions:', error);
    throw error;
  }
}
